export type ValidationIssueLike = {
  path: readonly PropertyKey[];
  message: string;
  code?: string;
  minimum?: number | bigint;
  maximum?: number | bigint;
};

export const linesFromText = (value: string) => value
  .split(/\r?\n/)
  .map((item) => item.trim())
  .filter(Boolean);

export const formCopy = {
  offer: {
    namePlaceholder: "Ingresá un nombre breve",
    nameHelp: "Obligatorio. Ejemplo: Servicio de soporte empresarial.",
    descriptionPlaceholder: "Describí la propuesta comercial",
    descriptionHelp: "Obligatorio. Indicá alcance, destinatarios y límites confirmados.",
    claimsPlaceholder: "Escribí un claim autorizado por línea",
    claimsHelp: "Obligatorio. Incluí sólo afirmaciones verificables.",
  },
  evidence: {
    namePlaceholder: "Ingresá un nombre descriptivo",
    nameHelp: "Obligatorio. Ejemplo: Presentación institucional vigente.",
    urlPlaceholder: "Ingresá una URL pública o interna autorizada",
    contentPlaceholder: "Resumí los hechos verificables de la fuente",
    sourceHelp: "Obligatorio: completá la URL, el contenido o ambos.",
    pdfDescriptionPlaceholder: "Describí brevemente el documento",
    pdfDescriptionHelp: "Ejemplo: Presentación comercial vigente.",
    pdfClaimsPlaceholder: "Escribí un claim autorizado por línea",
  },
  campaign: {
    namePlaceholder: "Ingresá un nombre que identifique la campaña",
    nameHelp: "Obligatorio. Ejemplo: Alianzas regionales · segundo semestre.",
    descriptionPlaceholder: "Resumí el alcance de esta campaña",
    descriptionHelp: "Opcional. No repitas reglas generales del sistema.",
    objectivePlaceholder: "Describí el resultado comercial esperado",
    objectiveHelp: "Obligatorio. Ejemplo: Identificar oportunidades y generar conversaciones calificadas.",
    defaultHelp: "Valor predeterminado del sistema. Podés modificarlo antes de guardar.",
  },
  direction: {
    reasonPlaceholder: "Explicá el fundamento de la decisión",
    reasonHelp: "Obligatorio para reformular o detener; mínimo 8 caracteres.",
    conditionsPlaceholder: "Escribí una condición por línea",
    conditionsHelp: "Se usa sólo al avanzar con condiciones y no amplía los claims autorizados.",
    retryPlaceholder: "Indicá qué debe ajustarse en la nueva versión",
  },
  meeting: {
    calendarIdPlaceholder: "Ingresá el ID del calendario",
    organizerPlaceholder: "Ingresá el email del organizador",
    attendeePlaceholder: "Ingresá el email del invitado",
    requiredWhenEnabled: "Obligatorio cuando la coordinación de reuniones está habilitada.",
    defaultsHelp: "Valor predeterminado de agenda. Podés modificarlo antes de guardar.",
  },
} as const;

const pathLabels: Record<string, string> = {
  name: "Nombre",
  description: "Descripción",
  offerId: "Oferta",
  "config.objective": "Objetivo",
  "config.icp.industries": "Industrias",
  "config.icp.countries": "Países",
  "config.icp.employeeMin": "Empleados mínimo",
  "config.icp.employeeMax": "Empleados máximo",
  "config.icp.targetRoles": "Roles objetivo",
  "config.goal.targetCompanies": "Empresas objetivo",
  "config.goal.targetContacts": "Contactos objetivo",
  "config.goal.targetMeetings": "Reuniones meta",
  "config.limits.maxEmailsPerDay": "Correos máximos por día",
  "config.schedule.startsAt": "Inicio de campaña",
  "config.schedule.outreachEndsAt": "Fin de primeros contactos",
  "config.schedule.timezone": "Zona horaria",
  "config.schedule.enabledWeekdays": "Días habilitados",
  "config.schedule.sendWindowStart": "Inicio de ventana",
  "config.schedule.sendWindowEnd": "Fin de ventana",
  "config.schedule.monitoringDays": "Días de monitoreo",
  "config.followUpPolicy": "Política de seguimiento",
  "config.followUpPolicy.firstBusinessDay": "Primer seguimiento",
  "config.followUpPolicy.secondBusinessDay": "Segundo seguimiento",
  "config.followUpPolicy.closeBusinessDay": "Cierre sin respuesta",
  "config.regions": "Regiones",
  "config.tone": "Tono",
  "config.enabledChannels": "Canales habilitados",
  "config.meeting.calendarId": "Calendar ID",
  "config.meeting.organizerEmail": "Email organizador",
  "config.meeting.timezone": "Zona horaria de agenda",
  "config.meeting.durationMinutes": "Duración",
  "config.meeting.workdayStart": "Inicio de jornada",
  "config.meeting.workdayEnd": "Fin de jornada",
  "config.meeting.minimumNoticeHours": "Anticipación mínima",
  "config.meeting.optionCount": "Cantidad de opciones",
  "config.meeting.searchHorizonDays": "Horizonte de búsqueda",
  claims: "Claims autorizados",
};

export function translateValidationMessage(message: string, issue?: ValidationIssueLike) {
  const normalized = message.trim();
  const minimum = issue?.minimum == null ? undefined : Number(issue.minimum);
  const maximum = issue?.maximum == null ? undefined : Number(issue.maximum);
  const path = issue?.path.map(String).join(".") ?? "";

  if (/^Too small:/i.test(normalized)) {
    const parsedMinimum = minimum ?? Number(/>=\s*(\d+)/.exec(normalized)?.[1]);
    if (/expected (?:number|int)/i.test(normalized))
      return Number.isFinite(parsedMinimum) ? `Ingresá un valor de ${parsedMinimum} o más.` : "Ingresá un valor mayor.";
    if (/expected array/i.test(normalized))
      return Number.isFinite(parsedMinimum) ? `Seleccioná al menos ${parsedMinimum} opción${parsedMinimum === 1 ? "" : "es"}.` : "Seleccioná al menos una opción.";
    return Number.isFinite(parsedMinimum)
      ? `Ingresá al menos ${parsedMinimum} caracteres.`
      : "El valor ingresado es demasiado corto.";
  }
  if (/^Too big:/i.test(normalized)) {
    const parsedMaximum = maximum ?? Number(/<=\s*(\d+)/.exec(normalized)?.[1]);
    if (/expected (?:number|int)/i.test(normalized))
      return Number.isFinite(parsedMaximum) ? `Ingresá un valor de ${parsedMaximum} o menos.` : "Ingresá un valor menor.";
    if (/expected array/i.test(normalized))
      return Number.isFinite(parsedMaximum) ? `Seleccioná como máximo ${parsedMaximum} opciones.` : "Seleccioná menos opciones.";
    return Number.isFinite(parsedMaximum)
      ? `Ingresá como máximo ${parsedMaximum} caracteres.`
      : "El valor ingresado supera el máximo permitido.";
  }
  if (/invalid.*email|email.*invalid/i.test(normalized)) return "Ingresá un email válido.";
  if (/invalid.*url|url.*invalid/i.test(normalized)) return "Ingresá una URL válida.";
  if (/invalid.*date|datetime/i.test(normalized)) return "Ingresá una fecha y hora válidas.";
  if (/targetMeetings.*targetContacts/i.test(normalized)) return "La meta de reuniones no puede superar la cantidad de contactos.";
  if (/outreachEndsAt.*startsAt/i.test(normalized)) return "El fin de primeros contactos debe ser posterior al inicio de campaña.";
  if (/invalid input|expected (?:string|number|array|object)|received (?:undefined|null)/i.test(normalized))
    return "Ingresá un valor válido.";
  if (/unrecognized key|validation|invalid_type|too_small|too_big/i.test(normalized))
    return "Revisá el valor ingresado.";
  if (!normalized) return "Revisá el valor ingresado.";
  if (path && /^[a-z][A-Za-z]+\s/.test(normalized)) return normalized;
  return normalized;
}

export function validationFieldMap(issues: readonly ValidationIssueLike[]) {
  const result: Record<string, string> = {};
  for (const issue of issues) {
    const path = issue.path.map(String).join(".");
    if (!path || result[path]) continue;
    result[path] = translateValidationMessage(issue.message, issue);
  }
  return result;
}

export function validationSummary(fieldErrors: Record<string, string>, extra: readonly string[] = []) {
  const fieldMessages = Object.entries(fieldErrors).map(([path, message]) => `${pathLabels[path] ?? "Campo"}: ${message}`);
  return [...new Set([...fieldMessages, ...extra.map((message) => translateValidationMessage(message))])];
}

export function visibleErrorMessage(error: unknown, fallback: string) {
  const message = error && typeof error === "object" && "message" in error && typeof error.message === "string"
    ? error.message
    : "";
  if (!message) return fallback;
  const translated = translateValidationMessage(message);
  return translated === "Revisá el valor ingresado." && message !== translated ? fallback : translated;
}
